"use client";

import React, { useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "../../../components/icons/X";
import { sanitizeHtml } from "../../../utils/sanitize";
import { defaultRenderImage } from "../../../utils/image/renderImage";
import {
  ModalCard,
  ModalClose,
  ModalNavButtons,
  ModalMedia,
  ModalNavButton,
  ModalNavRow,
  ModalOverlay,
  ModalScroll,
  ModalStage,
  ModalText,
  ModalTextColumn,
  ModalTitle,
} from "../DynamicHighlights.styles";
import { arrowGlyphShape, arrowStyle, isBareArrow } from "../arrowStyle";
import { ArrowGlyph } from "./ArrowGlyph";
import { CarouselIndicator } from "./CarouselIndicator";
import type {
  DynamicHighlightsProps,
  HighlightsArrowType,
  HighlightsIndicatorType,
  HighlightItem,
} from "../types";
import { ARIA_CLOSE, ARIA_NEXT, ARIA_PREV } from "../strings";

const FOCUSABLE =
  'button:not([disabled]), a[href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

/** Distância mínima do swipe horizontal para trocar de item no mobile. */
const SWIPE_THRESHOLD_PX = 48;

export type HighlightModalProps = {
  /** Itens navegáveis — só os que têm descrição completa. */
  items: HighlightItem[];
  /** Índice do item aberto dentro de `items`. `null` = fechado. */
  index: number | null;
  onClose: () => void;
  onNavigate: (nextIndex: number) => void;
  bgColor?: string;
  titleColor?: string;
  textColor?: string;
  /**
   * Cor do X, das setas e do indicador da janela. Vazio = cor atual: o X fica
   * branco (contraste garantido sobre o overlay escuro) e setas/indicador seguem
   * `navigationColor`.
   */
  iconColor?: string;
  /** Tipo de seta escolhido no accordion Formato — vale também aqui. */
  arrowType?: HighlightsArrowType;
  /** Cor da navegação do bloco: as setas do rodapé a seguem. */
  navigationColor?: string;
  /** Tipo de indicador escolhido no sidebar — o rodapé desenha o mesmo. */
  indicatorType?: HighlightsIndicatorType;
  renderImage?: DynamicHighlightsProps["renderImage"];
  /**
   * Classe da paleta ativa. O modal vai para o `body` por portal, FORA da
   * árvore da seção — sem esta classe nenhuma CSS var do styleguide (cores e
   * tipografia) resolve aqui dentro e tudo cai no valor inicial (fundo
   * transparente, fonte do documento).
   */
  activePalette?: string | null;
};

export const HighlightModal = ({
  items,
  index,
  onClose,
  onNavigate,
  activePalette,
  bgColor,
  titleColor,
  textColor,
  iconColor,
  arrowType = "outline",
  navigationColor = "var(--text-title)",
  indicatorType = "bar",
  renderImage = defaultRenderImage,
}: HighlightModalProps) => {
  // A cor dos ícones da janela, quando definida, vence o padrão: o X (branco) e
  // as setas/indicador (na cor da navegação).
  const navColor = iconColor || navigationColor;
  /*
   * Envolve card + botão de fechar: o X vive FORA do card (acima da borda), e
   * uma ref só no card o deixaria fora do focus trap — inalcançável por teclado.
   */
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const touchStartX = useRef<number | null>(null);
  const isOpen = index !== null && index >= 0 && index < items.length;

  const goTo = useCallback(
    (next: number) => {
      if (items.length === 0) return;
      const clamped = Math.min(Math.max(next, 0), items.length - 1);
      onNavigate(clamped);
    },
    [items.length, onNavigate],
  );

  // Esc fecha, setas navegam, Tab fica preso dentro do modal.
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key === "ArrowLeft") {
        goTo((index as number) - 1);
        return;
      }
      if (event.key === "ArrowRight") {
        goTo((index as number) + 1);
        return;
      }
      if (event.key !== "Tab") return;

      const dialog = dialogRef.current;
      if (!dialog) return;
      const focusables = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    document.body.style.overflow = "hidden";
    // Foco entra no modal ao abrir e volta para quem abriu ao fechar.
    dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [isOpen, index, goTo, onClose]);

  if (!isOpen || typeof document === "undefined") return null;

  const item = items[index as number];
  const hasPrev = (index as number) > 0;
  const hasNext = (index as number) < items.length - 1;
  const showNav = items.length > 1;
  const bare = isBareArrow(arrowType);
  const glyphShape = arrowGlyphShape(arrowType);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) onClose();
  };

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    touchStartX.current = event.touches[0]?.clientX ?? null;
  };

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const start = touchStartX.current;
    touchStartX.current = null;
    if (start === null) return;
    const delta = (event.changedTouches[0]?.clientX ?? start) - start;
    if (Math.abs(delta) < SWIPE_THRESHOLD_PX) return;
    goTo((index as number) + (delta < 0 ? 1 : -1));
  };

  return createPortal(
    <ModalOverlay
      onClick={handleOverlayClick}
      data-testid="dynamic-highlights-modal"
      className={activePalette || undefined}
    >
      <ModalStage ref={dialogRef}>
        {/*
          O X é IRMÃO do card, não filho: ele fica acima da borda superior
          (top negativo), e o card tem `overflow: hidden` para recortar a imagem
          no próprio raio — lá dentro ele era simplesmente recortado, invisível.
        */}
        <ModalClose type="button" aria-label={ARIA_CLOSE} onClick={onClose}>
          <X size={18} color={iconColor || "currentColor"} />
        </ModalClose>

        <ModalCard
          $bg={bgColor}
          role="dialog"
          aria-modal="true"
          aria-label={item.title}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          <ModalMedia>{renderImage({ src: item.image, alt: item.title })}</ModalMedia>

          {/* Texto rolando em cima, setas ancoradas no rodapé da mesma coluna. */}
          <ModalTextColumn>
            <ModalScroll>
              <ModalTitle titleTag="h3" $color={titleColor}>
                {item.title}
              </ModalTitle>
              <ModalText
                $color={textColor}
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(item.extendedDescription) }}
              />
            </ModalScroll>

            {showNav && (
              <ModalNavRow>
                {/*
                  O indicador segue o tipo escolhido no sidebar do bloco — o
                  mesmo do carrossel. Antes era um contador fixo, que ignorava
                  a escolha do Champion.
                */}
                <CarouselIndicator
                  type={indicatorType}
                  count={items.length}
                  index={index as number}
                  color={navColor}
                  onGoTo={goTo}
                />
                <ModalNavButtons $bare={bare}>
                  <ModalNavButton
                    type="button"
                    aria-label={ARIA_PREV}
                    onClick={() => goTo((index as number) - 1)}
                    disabled={!hasPrev}
                    $bare={bare}
                    style={arrowStyle(arrowType, navColor)}
                  >
                    <ArrowGlyph shape={glyphShape} />
                  </ModalNavButton>
                  <ModalNavButton
                    type="button"
                    aria-label={ARIA_NEXT}
                    onClick={() => goTo((index as number) + 1)}
                    disabled={!hasNext}
                    $bare={bare}
                    style={arrowStyle(arrowType, navColor)}
                  >
                    <ArrowGlyph shape={glyphShape} flip />
                  </ModalNavButton>
                </ModalNavButtons>
              </ModalNavRow>
            )}
          </ModalTextColumn>
        </ModalCard>
      </ModalStage>
    </ModalOverlay>,
    document.body,
  );
};
